import type { User } from '@supabase/supabase-js';
import { supabase } from './client';

export const NULL_TENANT_ID = '00000000-0000-0000-0000-000000000000';

interface TenantClaims {
  tenant_id?: string;
  tenant_slug?: string;
  role?: string;
  is_assumed?: boolean;
}

export function tenantClaimsOf(user: User | null | undefined): TenantClaims {
  const meta = (user?.app_metadata ?? {}) as Record<string, unknown>;
  return {
    tenant_id: typeof meta.tenant_id === 'string' ? meta.tenant_id : undefined,
    tenant_slug: typeof meta.tenant_slug === 'string' ? meta.tenant_slug : undefined,
    role: typeof meta.role === 'string' ? meta.role : undefined,
    is_assumed: typeof meta.is_assumed === 'boolean' ? meta.is_assumed : undefined,
  };
}

/**
 * Auth principal stamped server-side in app_metadata. Customer-portal logins
 * carry 'customer' and have no profiles/user_roles/tenant claim; staff carry
 * nothing (undefined).
 */
export function principalOf(user: User | null | undefined): string | undefined {
  const meta = (user?.app_metadata ?? {}) as Record<string, unknown>;
  return typeof meta.principal === 'string' ? meta.principal : undefined;
}

export function isCustomerUser(user: User | null | undefined): boolean {
  return principalOf(user) === 'customer';
}

/** Tenant id from the JWT claim; undefined when absent or the null-UUID placeholder. */
export function tenantIdOf(user: User | null | undefined): string | undefined {
  const id = tenantClaimsOf(user).tenant_id;
  return id && id !== NULL_TENANT_ID ? id : undefined;
}

/** Local session read (no network); null when signed out. */
export async function getSessionUser(): Promise<User | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user ?? null;
}

export async function requireSessionUser(): Promise<User> {
  const user = await getSessionUser();
  if (!user) throw new Error('Not authenticated');
  return user;
}

const profileTenantCache = new Map<string, string>();

/**
 * Claim first; falls back to profiles.company_id (fetched once per user per
 * tab) for a token issued before the claim was stamped.
 */
export async function resolveTenantId(user: User): Promise<string | undefined> {
  const claimed = tenantIdOf(user);
  if (claimed) return claimed;

  const cached = profileTenantCache.get(user.id);
  if (cached) return cached;

  const { data } = await supabase
    .from('profiles')
    .select('company_id')
    .eq('id', user.id)
    .maybeSingle();

  const id = data?.company_id && data.company_id !== NULL_TENANT_ID ? data.company_id : undefined;
  if (id) profileTenantCache.set(user.id, id);
  return id;
}

export async function requireTenantUser(): Promise<{ user: User; tenantId: string }> {
  const user = await requireSessionUser();
  const tenantId = await resolveTenantId(user);
  if (!tenantId) throw new Error('No company found');
  return { user, tenantId };
}

export async function requireTenantId(): Promise<string> {
  return (await requireTenantUser()).tenantId;
}
